/**
 * RemoteDeviceCenter — 连接中心
 * 统一管理 SSH服务器、API密钥、阿里云、Cloudflare、本地设备
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import './RemoteDeviceCenter.css';

// ============================================================
// Types
// ============================================================

export type ConnType = 'ssh' | 'api' | 'aliyun' | 'cloudflare';

interface BaseConn {
  id: string;
  name: string;
  type: ConnType;
  description?: string;
  tags: string[];
  createdAt: number;
  lastTested?: number;
  lastStatus?: 'ok' | 'error' | 'unknown';
}

export interface SSHConn extends BaseConn {
  type: 'ssh';
  host: string;
  port: number;
  user: string;
  authType: 'key' | 'password';
  keyPath?: string;
  role: 'dev' | 'staging' | 'production' | 'database' | 'local' | 'other';
  defaultWorkDir?: string;
}

export interface APIConn extends BaseConn {
  type: 'api';
  service: 'openai' | 'anthropic' | 'deepseek' | 'gemini' | 'qwen' | 'custom';
  baseUrl?: string;
  apiKey: string;
  defaultModel?: string;
  /** 设为 true 后，AI 路由的第三方模式将通过此连接转发 */
  isActiveRelay?: boolean;
}

export interface AliyunConn extends BaseConn {
  type: 'aliyun';
  region: string;
  accessKeyId: string;
  accessKeySecret: string;
}

export interface CloudflareConn extends BaseConn {
  type: 'cloudflare';
  email?: string;
  accountId: string;
  apiToken: string;
}

export type Connection = SSHConn | APIConn | AliyunConn | CloudflareConn;

// ============================================================
// Constants
// ============================================================

const TYPE_META: Record<ConnType, { label: string; icon: string; desc: string; color: string }> = {
  ssh:        { label: '服务器',      icon: '🖥️',  desc: '通过 SSH 连接远程服务器，自动执行命令、部署代码', color: '#30D158' },
  api:        { label: 'API 密钥',    icon: '🔑',  desc: 'OpenAI、Anthropic、DeepSeek 等 AI 接口',          color: '#007AFF' },
  aliyun:     { label: '阿里云',      icon: '☁️',  desc: 'AccessKey 管理 ECS、OSS、CDN 等服务',              color: '#FF6A00' },
  cloudflare: { label: 'Cloudflare', icon: '🌐',  desc: 'Workers、Pages、DNS、R2 等服务管理',               color: '#F48120' },
};

const SSH_ROLE_LABELS: Record<SSHConn['role'], string> = {
  dev: '开发', staging: '预发', production: '生产', database: '数据库', local: '本地电脑', other: '其他',
};
const SSH_ROLE_COLORS: Record<SSHConn['role'], string> = {
  dev: '#30D158', staging: '#FF9F0A', production: '#FF453A', database: '#5E5CE6', local: '#5E5CE6', other: '#8E8E93',
};

const API_SERVICE_META: Record<APIConn['service'], { label: string; color: string; defaultBase?: string }> = {
  openai:     { label: 'OpenAI',      color: '#00A67E' },
  anthropic:  { label: 'Anthropic',   color: '#D97757' },
  deepseek:   { label: 'DeepSeek',    color: '#4D6BFE' },
  gemini:     { label: 'Gemini',      color: '#1E73F3' },
  qwen:       { label: '通义千问',    color: '#FF6A00' },
  custom:     { label: '自定义',      color: '#8E8E93' },
};

const ALIYUN_REGIONS = [
  'cn-hangzhou', 'cn-shanghai', 'cn-beijing', 'cn-shenzhen', 'cn-hongkong',
  'ap-southeast-1', 'ap-northeast-1', 'us-west-1', 'eu-central-1',
];

// ============================================================
// Helpers
// ============================================================

const fmtTime = (ts?: number) => {
  if (!ts) return '从未';
  const d = new Date(ts);
  return d.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const maskKey = (key?: string) => {
  if (!key || key.length < 10) return '***';
  return key.slice(0, 6) + '...' + key.slice(-4);
};

// ============================================================
// Card components
// ============================================================

function StatusDot({ status }: { status?: string }) {
  const cls = status === 'ok' ? 'rdc__dot--ok' : status === 'error' ? 'rdc__dot--err' : 'rdc__dot--unknown';
  return <span className={`rdc__dot ${cls}`} />;
}

function TypeBadge({ type }: { type: ConnType }) {
  const m = TYPE_META[type];
  return (
    <span className="rdc__type-badge" style={{ '--badge-color': m.color } as React.CSSProperties}>
      {m.icon} {m.label}
    </span>
  );
}

function SSHCard({ conn, onTest, onEdit, onDelete, testing, testResult }: CardProps & { conn: SSHConn }) {
  const status = testResult ? (testResult.ok ? 'ok' : 'error') : conn.lastStatus;
  return (
    <div className={`rdc__card rdc__card--${status}`}>
      <div className="rdc__card-head">
        <StatusDot status={status} />
        <span className="rdc__card-name">{conn.name}</span>
        <span className="rdc__card-role-badge"
          style={{ '--role-color': SSH_ROLE_COLORS[conn.role] } as React.CSSProperties}>
          {SSH_ROLE_LABELS[conn.role]}
        </span>
      </div>
      <div className="rdc__card-addr">
        <span className="rdc__addr-user">{conn.user}@</span>
        <span className="rdc__addr-host">{conn.host}</span>
        {conn.port !== 22 && <span className="rdc__addr-port">:{conn.port}</span>}
      </div>
      {conn.description && <div className="rdc__card-desc">{conn.description}</div>}
      <div className="rdc__card-meta">
        <span>{conn.authType === 'key' ? '🔑 密钥认证' : '🔒 密码认证'}</span>
        {conn.defaultWorkDir && <span>📂 {conn.defaultWorkDir}</span>}
      </div>
      {conn.tags.length > 0 && (
        <div className="rdc__card-tags">{conn.tags.map(t => <span key={t} className="rdc__tag">{t}</span>)}</div>
      )}
      {testResult && (
        <div className={`rdc__test-result rdc__test-result--${testResult.ok ? 'ok' : 'err'}`}>
          {testResult.ok ? '✓' : '✗'} {testResult.msg.slice(0, 100)}
        </div>
      )}
      <CardFooter conn={conn} onTest={onTest} onEdit={onEdit} onDelete={onDelete} testing={testing} testResult={testResult} />
    </div>
  );
}

function APICard({ conn, onTest, onEdit, onDelete, onSetRelay, onUnsetRelay, testing, testResult }: CardProps & { conn: APIConn }) {
  const svc = API_SERVICE_META[conn.service];
  const status = testResult ? (testResult.ok ? 'ok' : 'error') : conn.lastStatus;
  return (
    <div className={`rdc__card rdc__card--${status}${conn.isActiveRelay ? ' rdc__card--relay' : ''}`}>
      <div className="rdc__card-head">
        <StatusDot status={status} />
        <span className="rdc__card-name">{conn.name}</span>
        <span className="rdc__type-badge" style={{ '--badge-color': svc.color } as React.CSSProperties}>
          {svc.label}
        </span>
        {conn.isActiveRelay && (
          <span className="rdc__relay-badge">⚡ 主用中转</span>
        )}
      </div>
      <div className="rdc__card-addr">
        <span className="rdc__addr-mono">{maskKey(conn.apiKey)}</span>
      </div>
      {conn.baseUrl && conn.service === 'custom' && (
        <div className="rdc__card-addr"><span className="rdc__addr-mono rdc__addr-dim">{conn.baseUrl}</span></div>
      )}
      {conn.defaultModel && (
        <div className="rdc__card-meta"><span>🤖 {conn.defaultModel}</span></div>
      )}
      {conn.description && <div className="rdc__card-desc">{conn.description}</div>}
      {conn.tags.length > 0 && (
        <div className="rdc__card-tags">{conn.tags.map(t => <span key={t} className="rdc__tag">{t}</span>)}</div>
      )}
      {testResult && (
        <div className={`rdc__test-result rdc__test-result--${testResult.ok ? 'ok' : 'err'}`}>
          {testResult.ok ? '✓' : '✗'} {testResult.msg.slice(0, 100)}
        </div>
      )}
      <CardFooter conn={conn} onTest={onTest} onEdit={onEdit} onDelete={onDelete} testing={testing} testResult={testResult} />
    </div>
  );
}

function AliyunCard({ conn, onTest, onEdit, onDelete, testing, testResult }: CardProps & { conn: AliyunConn }) {
  const status = testResult ? (testResult.ok ? 'ok' : 'error') : conn.lastStatus;
  return (
    <div className={`rdc__card rdc__card--${status}`}>
      <div className="rdc__card-head">
        <StatusDot status={status} />
        <span className="rdc__card-name">{conn.name}</span>
        <span className="rdc__type-badge" style={{ '--badge-color': '#FF6A00' } as React.CSSProperties}>
          ☁️ 阿里云
        </span>
      </div>
      <div className="rdc__card-addr">
        <span className="rdc__addr-mono">{maskKey(conn.accessKeyId)}</span>
        <span className="rdc__addr-dim"> · {conn.region}</span>
      </div>
      {conn.description && <div className="rdc__card-desc">{conn.description}</div>}
      {conn.tags.length > 0 && (
        <div className="rdc__card-tags">{conn.tags.map(t => <span key={t} className="rdc__tag">{t}</span>)}</div>
      )}
      {testResult && (
        <div className={`rdc__test-result rdc__test-result--${testResult.ok ? 'ok' : 'err'}`}>
          {testResult.ok ? '✓' : '✗'} {testResult.msg.slice(0, 100)}
        </div>
      )}
      <CardFooter conn={conn} onTest={onTest} onEdit={onEdit} onDelete={onDelete} testing={testing} testResult={testResult} />
    </div>
  );
}

function CloudflareCard({ conn, onTest, onEdit, onDelete, testing, testResult }: CardProps & { conn: CloudflareConn }) {
  const status = testResult ? (testResult.ok ? 'ok' : 'error') : conn.lastStatus;
  return (
    <div className={`rdc__card rdc__card--${status}`}>
      <div className="rdc__card-head">
        <StatusDot status={status} />
        <span className="rdc__card-name">{conn.name}</span>
        <span className="rdc__type-badge" style={{ '--badge-color': '#F48120' } as React.CSSProperties}>
          🌐 CF
        </span>
      </div>
      <div className="rdc__card-addr">
        <span className="rdc__addr-mono">{maskKey(conn.apiToken)}</span>
      </div>
      {conn.email && (
        <div className="rdc__card-meta"><span>✉️ {conn.email}</span></div>
      )}
      <div className="rdc__card-meta">
        <span>Account: {conn.accountId.slice(0, 8)}...</span>
      </div>
      {conn.description && <div className="rdc__card-desc">{conn.description}</div>}
      {conn.tags.length > 0 && (
        <div className="rdc__card-tags">{conn.tags.map(t => <span key={t} className="rdc__tag">{t}</span>)}</div>
      )}
      {testResult && (
        <div className={`rdc__test-result rdc__test-result--${testResult.ok ? 'ok' : 'err'}`}>
          {testResult.ok ? '✓' : '✗'} {testResult.msg.slice(0, 100)}
        </div>
      )}
      <CardFooter conn={conn} onTest={onTest} onEdit={onEdit} onDelete={onDelete} testing={testing} testResult={testResult} />
    </div>
  );
}


interface CardProps {
  onTest: (id: string) => void;
  onEdit: (conn: Connection) => void;
  onDelete: (id: string) => void;
  onTerminal?: (conn: SSHConn) => void;
  onSetRelay?: (id: string) => void;
  onUnsetRelay?: (id: string) => void;
  testing: boolean;
  testResult?: { ok: boolean; msg: string };
}

function CardFooter({ conn, onTest, onEdit, onDelete, onTerminal, onSetRelay, onUnsetRelay, testing }: CardProps & { conn: Connection }) {
  const isApiConn = conn.type === 'api';
  const isRelay = isApiConn && (conn as APIConn).isActiveRelay;
  return (
    <div className="rdc__card-footer">
      <span className="rdc__card-time">最近: {fmtTime(conn.lastTested)}</span>
      <div className="rdc__card-actions">
        {conn.type === 'ssh' && onTerminal && (
          <button className="rdc__btn rdc__btn--term rdc__btn--sm" onClick={() => onTerminal(conn as SSHConn)}>
            💻 终端
          </button>
        )}
        {isApiConn && (
          isRelay ? (
            <button className="rdc__btn rdc__btn--relay-active rdc__btn--sm" onClick={() => onUnsetRelay?.(conn.id)}>
              ⚡ 主用中
            </button>
          ) : (
            <button className="rdc__btn rdc__btn--ghost rdc__btn--sm" onClick={() => onSetRelay?.(conn.id)}>
              设为主用
            </button>
          )
        )}
        <button className="rdc__btn rdc__btn--ghost rdc__btn--sm" onClick={() => onTest(conn.id)} disabled={testing}>
          {testing ? '测试中...' : '测试'}
        </button>
        <button className="rdc__btn rdc__btn--ghost rdc__btn--sm" onClick={() => onEdit(conn)}>编辑</button>
        <button className="rdc__btn rdc__btn--danger rdc__btn--sm" onClick={() => onDelete(conn.id)}>删除</button>
      </div>
    </div>
  );
}

// ============================================================
// Forms per type
// ============================================================

function SSHForm({ form, setForm }: { form: any; setForm: any }) {
  return (
    <>
      <div className="rdc__row rdc__row--3">
        <div className="rdc__field rdc__field--grow">
          <label className="rdc__label">主机 / IP *</label>
          <input className="rdc__input" placeholder="192.168.1.100" value={form.host || ''}
            onChange={e => setForm((f: any) => ({ ...f, host: e.target.value }))} />
        </div>
        <div className="rdc__field">
          <label className="rdc__label">端口</label>
          <input className="rdc__input" type="number" value={form.port || 22}
            onChange={e => setForm((f: any) => ({ ...f, port: Number(e.target.value) }))} />
        </div>
        <div className="rdc__field rdc__field--grow">
          <label className="rdc__label">用户名 *</label>
          <input className="rdc__input" placeholder="root" value={form.user || ''}
            onChange={e => setForm((f: any) => ({ ...f, user: e.target.value }))} />
        </div>
      </div>
      <div className="rdc__field">
        <label className="rdc__label">角色</label>
        <select className="rdc__input rdc__select" value={form.role || 'other'}
          onChange={e => setForm((f: any) => ({ ...f, role: e.target.value }))}>
          {Object.entries(SSH_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="rdc__field">
        <label className="rdc__label">认证方式</label>
        <div className="rdc__toggle-group">
          <button type="button" className={`rdc__toggle ${form.authType === 'key' ? 'rdc__toggle--active' : ''}`}
            onClick={() => setForm((f: any) => ({ ...f, authType: 'key' }))}>🔑 SSH 密钥</button>
          <button type="button" className={`rdc__toggle ${form.authType === 'password' ? 'rdc__toggle--active' : ''}`}
            onClick={() => setForm((f: any) => ({ ...f, authType: 'password' }))}>🔒 密码</button>
        </div>
      </div>
      {form.authType === 'password' ? (
        <div className="rdc__field">
          <label className="rdc__label">密码</label>
          <input className="rdc__input" type="password" placeholder="SSH 登录密码"
            onChange={e => setForm((f: any) => ({ ...f, password: e.target.value }))} />
          <span className="rdc__hint-text">推荐使用密钥认证，密码认证需服务器安装 sshpass</span>
        </div>
      ) : (
        <div className="rdc__field">
          <label className="rdc__label">私钥路径</label>
          <input className="rdc__input" placeholder="~/.ssh/id_rsa" value={form.keyPath || ''}
            onChange={e => setForm((f: any) => ({ ...f, keyPath: e.target.value }))} />
        </div>
      )}
      <div className="rdc__field">
        <label className="rdc__label">默认工作目录</label>
        <input className="rdc__input" placeholder="/var/www/app（可选）" value={form.defaultWorkDir || ''}
          onChange={e => setForm((f: any) => ({ ...f, defaultWorkDir: e.target.value }))} />
      </div>
    </>
  );
}

function APIForm({ form, setForm }: { form: any; setForm: any }) {
  return (
    <>
      <div className="rdc__field">
        <label className="rdc__label">服务商</label>
        <select className="rdc__input rdc__select" value={form.service || 'openai'}
          onChange={e => setForm((f: any) => ({ ...f, service: e.target.value }))}>
          {Object.entries(API_SERVICE_META).map(([v, m]) => (
            <option key={v} value={v}>{m.label}</option>
          ))}
        </select>
      </div>
      <div className="rdc__field">
        <label className="rdc__label">API Key *</label>
        <input className="rdc__input" type="password" placeholder="sk-..." value={form.apiKey || ''}
          onChange={e => setForm((f: any) => ({ ...f, apiKey: e.target.value }))} />
      </div>
      <div className="rdc__field">
        <label className="rdc__label">Base URL（可选，留空使用官方端点）</label>
        <input className="rdc__input" placeholder="https://seekgt.com/v1  或  https://api.example.com/v1" value={form.baseUrl || ''}
          onChange={e => setForm((f: any) => ({ ...f, baseUrl: e.target.value }))} />
        <span className="rdc__hint-text">填写后将覆盖默认端点，可接入第三方代理（支持 Claude / Codex / Grok 等）</span>
      </div>
      <div className="rdc__field">
        <label className="rdc__label">默认模型（可选）</label>
        <input className="rdc__input" placeholder="gpt-4o / claude-opus-4-5 / grok-4.20-heavy / ..." value={form.defaultModel || ''}
          onChange={e => setForm((f: any) => ({ ...f, defaultModel: e.target.value }))} />
      </div>
    </>
  );
}

function AliyunForm({ form, setForm }: { form: any; setForm: any }) {
  return (
    <>
      <div className="rdc__field">
        <label className="rdc__label">地域 Region</label>
        <select className="rdc__input rdc__select" value={form.region || 'cn-hangzhou'}
          onChange={e => setForm((f: any) => ({ ...f, region: e.target.value }))}>
          {ALIYUN_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div className="rdc__field">
        <label className="rdc__label">Access Key ID *</label>
        <input className="rdc__input" placeholder="LTAIxxxxxx" value={form.accessKeyId || ''}
          onChange={e => setForm((f: any) => ({ ...f, accessKeyId: e.target.value }))} />
      </div>
      <div className="rdc__field">
        <label className="rdc__label">Access Key Secret *</label>
        <input className="rdc__input" type="password" placeholder="Access Key Secret" value={form.accessKeySecret || ''}
          onChange={e => setForm((f: any) => ({ ...f, accessKeySecret: e.target.value }))} />
      </div>
      <span className="rdc__hint-text">在阿里云控制台 → 右上角头像 → AccessKey 管理 中获取</span>
    </>
  );
}

function CloudflareForm({ form, setForm }: { form: any; setForm: any }) {
  return (
    <>
      <div className="rdc__field">
        <label className="rdc__label">账户邮箱</label>
        <input className="rdc__input" type="email" placeholder="you@example.com" value={form.email || ''}
          onChange={e => setForm((f: any) => ({ ...f, email: e.target.value }))} />
      </div>
      <div className="rdc__field">
        <label className="rdc__label">Account ID *</label>
        <input className="rdc__input" placeholder="32位 Account ID" value={form.accountId || ''}
          onChange={e => setForm((f: any) => ({ ...f, accountId: e.target.value }))} />
      </div>
      <div className="rdc__field">
        <label className="rdc__label">API Token *</label>
        <input className="rdc__input" type="password" placeholder="API Token（推荐使用自定义 Token）" value={form.apiToken || ''}
          onChange={e => setForm((f: any) => ({ ...f, apiToken: e.target.value }))} />
        <span className="rdc__hint-text">Cloudflare Dashboard → My Profile → API Tokens → Create Token</span>
      </div>
    </>
  );
}


// ============================================================
// Tags input
// ============================================================

function TagsInput({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [input, setInput] = useState('');
  const add = () => {
    const t = input.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput('');
  };
  return (
    <div className="rdc__field">
      <label className="rdc__label">标签（可选）</label>
      <div className="rdc__tag-input-row">
        <input className="rdc__input rdc__input--tag" placeholder="输入标签后按 Enter" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <button type="button" className="rdc__btn rdc__btn--ghost rdc__btn--sm" onClick={add}>添加</button>
      </div>
      {tags.length > 0 && (
        <div className="rdc__tags-preview">
          {tags.map(t => (
            <span key={t} className="rdc__tag rdc__tag--removable"
              onClick={() => onChange(tags.filter(x => x !== t))}>
              {t} ×
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Terminal Modal
// ============================================================

interface TermEntry { type: 'cmd' | 'stdout' | 'stderr' | 'exit' | 'info'; text: string }

function TerminalModal({ conn, onClose }: { conn: SSHConn; onClose: () => void }) {
  const [history, setHistory] = useState<TermEntry[]>([
    { type: 'info', text: `已连接 ${conn.user}@${conn.host}:${conn.port}  (输入 clear 清屏)` },
  ]);
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const [cmdHist, setCmdHist] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [history]);

  const runCmd = async () => {
    const cmd = input.trim();
    if (!cmd || running) return;
    setInput('');
    setHistIdx(-1);

    if (cmd === 'clear') { setHistory([]); return; }

    setCmdHist(h => [cmd, ...h.slice(0, 49)]);
    setHistory(h => [...h, { type: 'cmd', text: cmd }]);
    setRunning(true);

    try {
      const res = await fetch(`/api/connections/${conn.id}/exec-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: cmd }),
      });

      if (!res.ok || !res.body) {
        setHistory(h => [...h, { type: 'stderr', text: `[错误] HTTP ${res.status}` }]);
        setRunning(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const msg = JSON.parse(line.slice(6)) as { type: string; data: string };
            if (msg.type === 'exit') {
              if (msg.data !== '0') {
                setHistory(h => [...h, { type: 'stderr', text: `[exit ${msg.data}]` }]);
              }
            } else {
              setHistory(h => [...h, { type: msg.type as TermEntry['type'], text: msg.data }]);
            }
          } catch {}
        }
      }
    } catch (err) {
      setHistory(h => [...h, { type: 'stderr', text: `[错误] ${err instanceof Error ? err.message : '连接失败'}` }]);
    } finally {
      setRunning(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { runCmd(); return; }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = Math.min(histIdx + 1, cmdHist.length - 1);
      setHistIdx(idx);
      setInput(cmdHist[idx] ?? '');
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = Math.max(histIdx - 1, -1);
      setHistIdx(idx);
      setInput(idx === -1 ? '' : cmdHist[idx]);
    }
  };

  return (
    <div className="rdc__term-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="rdc__term">
        <div className="rdc__term-head">
          <div className="rdc__term-head-dots">
            <span style={{ background: '#FF5F57' }} onClick={onClose} />
            <span style={{ background: '#FFBD2E' }} />
            <span style={{ background: '#28C840' }} />
          </div>
          <span className="rdc__term-title">{conn.name}</span>
          <span className="rdc__term-addr">{conn.user}@{conn.host}</span>
        </div>
        <div className="rdc__term-output" ref={outputRef} onClick={() => inputRef.current?.focus()}>
          {history.map((e, i) => (
            <div key={i} className={`rdc__term-line rdc__term-line--${e.type}`}>
              {e.type === 'cmd' && <span className="rdc__term-ps">$ </span>}
              <span>{e.text}</span>
            </div>
          ))}
          {running && <span className="rdc__term-blink">▌</span>}
        </div>
        <div className="rdc__term-row">
          <span className="rdc__term-ps">$ </span>
          <input
            ref={inputRef}
            className="rdc__term-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={running}
            placeholder={running ? '执行中…' : ''}
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main component
// ============================================================

type Tab = ConnType | 'all' | 'local';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'all',        label: '全部',       icon: '⊞' },
  { key: 'ssh',        label: '服务器',     icon: '🖥️' },
  { key: 'api',        label: 'API 密钥',   icon: '🔑' },
  { key: 'aliyun',     label: '阿里云',     icon: '☁️' },
  { key: 'cloudflare', label: 'Cloudflare', icon: '🌐' },
  { key: 'local',      label: '本地电脑',   icon: '💻' },
];

export default function RemoteDeviceCenter() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('all');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [step, setStep] = useState<'type' | 'form'>('type');
  const [selectedType, setSelectedType] = useState<ConnType>('ssh');
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  // Test state
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [terminalConn, setTerminalConn] = useState<SSHConn | null>(null);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await fetch('/api/connections');
      const data = await res.json();
      if (data.success) setConnections(data.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const filtered = activeTab === 'all' ? connections
    : activeTab === 'local' ? connections.filter(c => c.type === 'ssh' && (c as SSHConn).role === 'local')
    : connections.filter(c => c.type === activeTab);

  const countByType = (t: Tab) =>
    t === 'all' ? connections.length
    : t === 'local' ? connections.filter(c => c.type === 'ssh' && (c as SSHConn).role === 'local').length
    : connections.filter(c => c.type === t).length;

  // Open modal for new connection
  const openAdd = () => {
    setEditingId(null);
    // If on 'local' tab, pre-select SSH + role=local
    if (activeTab === 'local') {
      setSelectedType('ssh');
      setStep('form');
      setForm({ authType: 'key', port: 22, role: 'local', tags: [] });
    } else {
      setStep('type');
      setSelectedType(activeTab === 'all' ? 'ssh' : activeTab as ConnType);
      setForm({ authType: 'key', port: 22, role: 'other', tags: [], service: 'openai', region: 'cn-hangzhou' });
    }
    setShowModal(true);
  };

  // Open modal for editing
  const openEdit = (conn: Connection) => {
    setEditingId(conn.id);
    setSelectedType(conn.type);
    setStep('form');
    setForm({ ...conn });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingId(null); };

  const proceedToForm = () => {
    const defaults: Record<ConnType, any> = {
      ssh:        { authType: 'key', port: 22, role: 'other', tags: [] },
      api:        { service: 'openai', tags: [] },
      aliyun:     { region: 'cn-hangzhou', tags: [] },
      cloudflare: { tags: [] },
    };
    setForm(defaults[selectedType]);
    setStep('form');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingId ? `/api/connections/${editingId}` : '/api/connections';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: selectedType, name: form.name }),
      });
      const data = await res.json();
      if (data.success) {
        await fetchConnections();
        closeModal();
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await fetch(`/api/connections/${id}`, { method: 'DELETE' });
      setConnections(c => c.filter(x => x.id !== id));
    } finally { setDeletingId(null); }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    setTestResults(r => ({ ...r, [id]: { ok: false, msg: '测试中...' } }));
    try {
      const res = await fetch(`/api/connections/${id}/test`, { method: 'POST' });
      const data = await res.json();
      setTestResults(r => ({
        ...r,
        [id]: { ok: data.success, msg: data.success ? data.data?.output || '成功' : data.error || '失败' },
      }));
      if (data.success) await fetchConnections();
    } catch {
      setTestResults(r => ({ ...r, [id]: { ok: false, msg: '请求失败' } }));
    } finally { setTestingId(null); }
  };

  const handleSetRelay = async (id: string) => {
    try {
      const res = await fetch(`/api/connections/${id}/set-relay`, { method: 'POST' });
      const data = await res.json();
      if (data.success) await fetchConnections();
    } catch { /* ignore */ }
  };

  const handleUnsetRelay = async (id: string) => {
    try {
      const res = await fetch(`/api/connections/${id}/set-relay`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) await fetchConnections();
    } catch { /* ignore */ }
  };

  // Form validation
  const canSave = () => {
    if (!form.name?.trim()) return false;
    if (selectedType === 'ssh') return !!(form.host?.trim() && form.user?.trim());
    if (selectedType === 'api') return !!form.apiKey?.trim();
    if (selectedType === 'aliyun') return !!(form.accessKeyId?.trim() && form.accessKeySecret?.trim());
    if (selectedType === 'cloudflare') return !!(form.apiToken?.trim() && form.accountId?.trim());
    return true;
  };

  return (
    <div className="rdc">
      {/* Header */}
      <div className="rdc__header">
        <div className="rdc__header-left">
          <span className="rdc__title">连接中心</span>
          <span className="rdc__count">{connections.length} 个连接</span>
        </div>
        <button className="rdc__btn rdc__btn--primary" onClick={openAdd}>+ 添加连接</button>
      </div>

      {/* Tabs */}
      <div className="rdc__tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`rdc__tab ${activeTab === tab.key ? 'rdc__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="rdc__tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
            <span className="rdc__tab-count">{countByType(tab.key)}</span>
          </button>
        ))}
      </div>

      {/* Hint */}
      <div className="rdc__hint">
        {activeTab === 'all' && '管理所有外部连接 — SSH 服务器、API 密钥、云服务账户。Work 模式会自动调用这些连接完成部署、API 调用等任务。'}
        {activeTab === 'ssh' && '通过 SSH 连接到远程服务器，支持密钥/密码认证。Work 模式用于执行命令、部署代码、管理文件。'}
        {activeTab === 'api' && '存储 AI 服务及第三方 API 的密钥。Work 模式根据任务类型自动选择合适的 API 调用。'}
        {activeTab === 'aliyun' && '阿里云 AccessKey，Work 模式可调用 ECS、OSS、函数计算等服务完成部署和运维。'}
        {activeTab === 'cloudflare' && 'Cloudflare API Token，Work 模式可部署 Workers/Pages、管理 DNS、上传到 R2 等。'}
        {activeTab === 'local' && '通过 SSH 连接你自己的电脑。在 SSH 表单中把角色选为"本地电脑"即可归类到这里。'}
      </div>

      {/* Grid */}
      <div className="rdc__body">
        {loading ? (
          <div className="rdc__empty"><div className="rdc__empty-title">加载中...</div></div>
        ) : filtered.length === 0 ? (
          <div className="rdc__empty">
            <div className="rdc__empty-icon">
              {activeTab === 'all' ? '🔌' : activeTab === 'local' ? '💻' : TYPE_META[activeTab as ConnType]?.icon || '🔌'}
            </div>
            <div className="rdc__empty-title">
              {activeTab === 'all' ? '尚未添加任何连接'
                : activeTab === 'local' ? '尚未添加本地电脑'
                : `尚未添加 ${TYPE_META[activeTab as ConnType]?.label} 连接`}
            </div>
            <div className="rdc__empty-desc">
              {activeTab === 'all' ? '点击"添加连接"开始配置 SSH、API、云服务等连接'
                : activeTab === 'local' ? '添加 SSH 连接并将角色设为"本地电脑"，即可在此处统一管理'
                : `点击"添加连接"配置 ${TYPE_META[activeTab as ConnType]?.label}`}
            </div>
            <button className="rdc__btn rdc__btn--primary" onClick={openAdd}>+ 添加连接</button>
          </div>
        ) : (
          <div className="rdc__grid">
            {filtered.map(conn => {
              const props = {
                key: conn.id,
                onTest: handleTest,
                onEdit: openEdit,
                onDelete: handleDelete,
                onTerminal: (c: SSHConn) => setTerminalConn(c),
                onSetRelay: handleSetRelay,
                onUnsetRelay: handleUnsetRelay,
                testing: testingId === conn.id,
                testResult: testResults[conn.id],
              };
              switch (conn.type) {
                case 'ssh':        return <SSHCard        {...props} conn={conn as SSHConn}        />;
                case 'api':        return <APICard        {...props} conn={conn as APIConn}        />;
                case 'aliyun':     return <AliyunCard     {...props} conn={conn as AliyunConn}     />;
                case 'cloudflare': return <CloudflareCard {...props} conn={conn as CloudflareConn} />;
                default:           return null;
              }
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="rdc__overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="rdc__modal">
            <div className="rdc__modal-head">
              <span className="rdc__modal-title">
                {editingId ? '编辑连接' : step === 'type' ? '选择连接类型' : `添加 ${TYPE_META[selectedType].label}`}
              </span>
              <button className="rdc__modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="rdc__modal-body">
              {/* Step 1: type picker */}
              {step === 'type' && !editingId && (
                <div className="rdc__type-grid">
                  {(Object.entries(TYPE_META) as [ConnType, typeof TYPE_META[ConnType]][]).map(([key, meta]) => (
                    <button
                      key={key}
                      type="button"
                      className={`rdc__type-option ${selectedType === key ? 'rdc__type-option--active' : ''}`}
                      style={{ '--type-color': meta.color } as React.CSSProperties}
                      onClick={() => setSelectedType(key)}
                    >
                      <span className="rdc__type-option-icon">{meta.icon}</span>
                      <span className="rdc__type-option-label">{meta.label}</span>
                      <span className="rdc__type-option-desc">{meta.desc}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2: form */}
              {(step === 'form' || editingId) && (
                <>
                  {/* Name + description (common) */}
                  <div className="rdc__row rdc__row--2">
                    <div className="rdc__field">
                      <label className="rdc__label">名称 *</label>
                      <input className="rdc__input" placeholder="如：主力生产服务器" value={form.name || ''}
                        onChange={e => setForm((f: any) => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="rdc__field">
                      <label className="rdc__label">备注</label>
                      <input className="rdc__input" placeholder="可选说明" value={form.description || ''}
                        onChange={e => setForm((f: any) => ({ ...f, description: e.target.value }))} />
                    </div>
                  </div>

                  {/* Type-specific fields */}
                  {selectedType === 'ssh'        && <SSHForm        form={form} setForm={setForm} />}
                  {selectedType === 'api'        && <APIForm        form={form} setForm={setForm} />}
                  {selectedType === 'aliyun'     && <AliyunForm     form={form} setForm={setForm} />}
                  {selectedType === 'cloudflare' && <CloudflareForm form={form} setForm={setForm} />}

                  {/* Tags */}
                  <TagsInput tags={form.tags || []} onChange={tags => setForm((f: any) => ({ ...f, tags }))} />
                </>
              )}
            </div>

            <div className="rdc__modal-footer">
              {step === 'type' && !editingId ? (
                <>
                  <button className="rdc__btn rdc__btn--ghost" onClick={closeModal}>取消</button>
                  <button className="rdc__btn rdc__btn--primary" onClick={proceedToForm}>
                    下一步 →
                  </button>
                </>
              ) : (
                <>
                  {!editingId && (
                    <button className="rdc__btn rdc__btn--ghost" onClick={() => setStep('type')}>← 返回</button>
                  )}
                  <button className="rdc__btn rdc__btn--ghost" onClick={closeModal}>取消</button>
                  <button className="rdc__btn rdc__btn--primary" onClick={handleSave}
                    disabled={saving || !canSave()}>
                    {saving ? '保存中...' : editingId ? '保存修改' : '添加'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SSH Terminal */}
      {terminalConn && (
        <TerminalModal conn={terminalConn} onClose={() => setTerminalConn(null)} />
      )}
    </div>
  );
}
